/**
 * Helpers for opening a "new email" composer pre-addressed to a recipient.
 *
 * Recruiters who don't want to use sequences can click an email and jump
 * straight into a draft in their preferred client (Gmail web, Outlook web,
 * or whatever the OS has registered for `mailto:`).
 */

export type MailClient = "google" | "microsoft" | "default";

export type ComposeFields = {
  subject?: string;
  body?: string;
  cc?: string;
  bcc?: string;
};

function appendParams(base: string, params: Record<string, string | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value);
  }
  const query = search.toString();
  return query ? `${base}${base.includes("?") ? "&" : "?"}${query}` : base;
}

/** Build the compose URL for a given mail client. */
export function buildComposeUrl(
  client: MailClient,
  email: string,
  fields: ComposeFields = {},
): string {
  const to = email.trim();
  const { subject, body, cc, bcc } = fields;

  switch (client) {
    case "google":
      return appendParams("https://mail.google.com/mail/?view=cm&fs=1", {
        to,
        su: subject,
        body,
        cc,
        bcc,
      });
    case "microsoft":
      return appendParams("https://outlook.office.com/mail/deeplink/compose", {
        to,
        subject,
        body,
        cc,
        bcc,
      });
    case "default":
    default:
      return appendParams(`mailto:${to}`, { subject, body, cc, bcc });
  }
}

/**
 * Open a pre-addressed composer. Web clients open in a new tab; the default
 * mail client is handed off via the `mailto:` protocol on the current tab so
 * the OS handler takes over without leaving the app.
 */
export function openCompose(
  client: MailClient,
  email: string,
  fields: ComposeFields = {},
): void {
  const url = buildComposeUrl(client, email, fields);
  if (client === "default") {
    window.location.href = url;
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}
